import { useEffect, useState } from 'react';
import { format } from '../../helpers/format';

import { useTimeRangeYears } from '../../hooks/use-timerangeyears';
import { DataOptions, GeoLevel, Indicator, TimeRangeMap } from '../../types';
import { DownloadIndicatorButton } from '../DownloadIndicatorButton';
import { useTranslation } from 'react-i18next';
import { SparkLine } from './SparkLine';
import { DataPoint } from '../../hooks/use-data2.hook';

const prioritizeKommun: string = import.meta.env.VITE_PRIORITIZE_KOMMUN ?? '';

type Props = {
    dataOptions: DataOptions;
    dataFeatures: DataPoint[];
    geoLevel: GeoLevel;
    timeRangeMap: TimeRangeMap;
    selectedFeatures: string[];
    featureFilter: string[];
    onSelectFeature: (selectedFeatures: string[]) => void;
};

export function Table({
    dataOptions,
    dataFeatures,
    geoLevel,
    timeRangeMap,
    selectedFeatures = [],
    onSelectFeature,
    featureFilter = [],
}: Props) {
    const { t, i18n } = useTranslation();

    const years = useTimeRangeYears({ dataOptions, timeRangeMap });

    const [selectedVar, setSelectedVar] = useState<Indicator>(
        dataOptions.indicators.xVar
    );

    useEffect(() => {
        setSelectedVar(dataOptions.indicators.xVar);
    }, [dataOptions]);

    const uniqoptions = Array.from(
        new Set(Object.values(dataOptions.indicators))
    ).filter((v) => v) as Indicator[];

    function HeaderRow() {
        return (
            <tr className="bg-base-200">
                <th className=""></th>
                <th className="">
                    <input
                        type="checkbox"
                        className="checkbox"
                        aria-label={t('select-all')}
                        checked={selectedFeatures.length > 0}
                        onChange={(e) => {
                            if (e.target.checked) {
                                onSelectFeature(
                                    dataFeatures.map(
                                        (f) => f.properties?.id ?? ''
                                    )
                                );
                            } else {
                                onSelectFeature([]);
                            }
                        }}
                    />
                </th>
                <th className="">{t('kommun')}</th>
                <th className="">{t('namn')}</th>
                <th className=""></th>
                {years.map((year) => {
                    return (
                        <th className="" key={year}>
                            {year}
                        </th>
                    );
                })}
            </tr>
        );
    }

    const sorted = dataFeatures
        .sort((a, b) => {
            if (geoLevel === 'kommun') {
                return 0;
            }
            return a.properties?.name.localeCompare(b.properties?.name, 'sv');
        })
        .sort((a, b) =>
            a.properties?.kommunnamn.localeCompare(
                b.properties?.kommunnamn,
                'sv'
            )
        );

    const selectedFeaturesSet = new Set(selectedFeatures);

    return (
        <div className="relative">
            <div className="flex flex-row justify-center">
                <div className="tabs tabs-bordered bg-white">
                    {uniqoptions.map((o) => {
                        const label = `${
                            o.category?.[i18n.language] ?? o.category?.['sv']
                        } - ${o.label[i18n.language] ?? o.label['sv']}, ${
                            o.subLabel[i18n.language] ?? o.subLabel['sv']
                        }${o.unitDisplay ? `, ${o.unitDisplay}` : ''}`;
                        return (
                            <div
                                key={o.id}
                                className={`tab flex gap-2 ${
                                    selectedVar.id === o.id ? 'tab-active' : ''
                                }`}
                            >
                                <button
                                    onClick={() => {
                                        setSelectedVar(o);
                                    }}
                                    className="flex items-center"
                                    title={label}
                                >
                                    <small className="truncate max-w-[30ch] inline-block">
                                        {label}
                                    </small>
                                </button>
                                <DownloadIndicatorButton
                                    data={{ points: dataFeatures }}
                                    geoLevel={dataOptions.geoLevel}
                                    indicator={o}
                                    featureFilter={featureFilter}
                                />
                            </div>
                        );
                    })}

                    <div className={` flex gap-1 items-center tab  underline`}>
                        <a
                            href={`${
                                import.meta.env.VITE_API_URL
                            }/api/v1/data/indicators/${geoLevel}/all.csv?${uniqoptions
                                .map((o) => `id=${o.id}`)
                                .join('&')}`}
                        >
                            {t('download-all')} (.csv)
                        </a>
                    </div>
                </div>
            </div>
            <table className="table table-xs table-pin-rows">
                <thead>
                    <HeaderRow />
                </thead>
                <tbody>
                    {(prioritizeKommun.length > 0
                        ? [
                              ...sorted.filter(
                                  (d) =>
                                      d.properties?.kommunnamn ===
                                      prioritizeKommun
                              ),
                              ...sorted.filter(
                                  (d) =>
                                      d.properties?.kommunnamn !==
                                      prioritizeKommun
                              ),
                          ]
                        : sorted
                    ).map((f, i) => {
                        return (
                            <tr
                                onClick={() => {
                                    const features = selectedFeatures.includes(
                                        f.properties?.id
                                    )
                                        ? selectedFeatures.filter(
                                              (id) => id !== f.properties?.id
                                          )
                                        : [
                                              ...selectedFeatures,
                                              f.properties?.id,
                                          ];
                                    onSelectFeature(features);
                                }}
                                className={`${
                                    selectedFeatures.includes(f.properties?.id)
                                        ? 'active'
                                        : ''
                                } cursor-pointer`}
                                key={`${f.properties?.kommunnamn}-${f.properties?.name}`}
                            >
                                <td>{i + 1}</td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={
                                            f.properties?.id
                                                ? selectedFeaturesSet.has(
                                                      f.properties.id
                                                  )
                                                : false
                                        }
                                        readOnly
                                        className="checkbox"
                                        aria-label={f.properties?.name ?? f.properties?.kommunnamn}
                                    />
                                </td>
                                <td>{f.properties?.kommunnamn}</td>
                                <td>{f.properties?.name}</td>
                                <td>
                                    <SparkLine
                                        data={years.map((year) => {
                                            return {
                                                x: year,
                                                y: f.properties?.values[
                                                    selectedVar.id
                                                ]?.[year] ?? null,
                                            };
                                        })}
                                    />
                                </td>
                                {years.map((year) => {
                                    const value = f.properties?.values[selectedVar.id]?.[year];
                                    if (value == null) {
                                        return <td key={year}>-</td>;
                                    }
                                    return (
                                        <td key={year}>
                                            {format(selectedVar.unitFormat)(value)}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <HeaderRow />
                </tfoot>
            </table>
        </div>
    );
}
